import React, { useCallback } from 'react';
import styled from 'styled-components';
import { LicenseKeyListItemType } from '@/api/type/types';
import { fonts } from '@/theme/fonts';
import { Images } from '@/assets';
import { formatDate } from '@/utils/date-time/formatDate';
import { deleteRevokeLicenseKey } from '@/api/license-list/deleteRevokeLicenseKey';
import { useDispatch } from 'react-redux';
import { license } from '@/stores/slices/license-list/licenseSlice';
import { useTranslation } from 'react-i18next';
import LicenseKeyMemoCell from './LicenseKeyMemoCell';
import { postAddLicenseKeyMemo } from '@/api/license-list/postAddLicenseKeyMemo';
import { licenseErrorHandler } from '@/service/error/license/licenseErrorHandler';
import { useAlert } from '@/hook/useAlert';

interface LicenseKeyListItemProps {
  items: LicenseKeyListItemType[];
  isDeleteMode: boolean;
}

const LicenseKeyListItem = ({
  items,
  isDeleteMode,
}: LicenseKeyListItemProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { showAlert } = useAlert();

  const baseColumns = [
    {
      key: 'no',
      label: t('licenseManagement.number'),
      width: '5%',
      align: 'center',
    },
    {
      key: 'licenseKeyId',
      label: t('licenseManagement.licenseKey'),
      width: '18%',
      align: 'center',
    },
    {
      key: 'module',
      label: t('licenseManagement.moduleName'),
      width: '8%',
      align: 'center',
    },
    {
      key: 'status',
      label: t('licenseManagement.statusLabel'),
      width: '8%',
      align: 'center',
    },
    {
      key: 'createdAt',
      label: t('licenseManagement.createdDate'),
      width: '9%',
      align: 'center',
    },
    {
      key: 'registeredAt',
      label: t('licenseManagement.registeredDate'),
      width: '9%',
      align: 'center',
    },
    {
      key: 'revokedAt',
      label: t('licenseManagement.revokedDate'),
      width: '9%',
      align: 'center',
    },
    {
      key: 'usedByMemberLoginId',
      label: t('licenseManagement.registeredId'),
      width: '15%',
      align: 'center',
    },
    {
      key: 'issuerName',
      label: t('licenseManagement.issuer'),
      width: '8%',
      align: 'center',
    },
    {
      key: 'issuanceReason',
      label: t('licenseManagement.issueReason'),
      width: '16%',
      align: 'left',
    },
    {
      key: 'memo',
      label: t('licenseManagement.memo'),
      width: '12%',
      align: 'center',
    },
  ];

  const deleteColumn = {
    key: 'delete',
    label: '',
    width: '8%',
    align: 'center',
  } as const;

  const columns = [...baseColumns, deleteColumn];

  const onClickDelete = useCallback(
    (item: LicenseKeyListItemType) => async () => {
      const isConfirm = confirm(
        t('licenseManagement.messages.revokeConfirm', {
          module: item.module,
          licenseKeyId: item.licenseKeyId,
          usedByMemberLoginId: item.usedByMemberLoginId ?? '',
        })
      );
      if (isConfirm) {
        // 폐기 처리
        const res = await deleteRevokeLicenseKey({
          licenseKeyId: item.licenseKeyId,
        });
        if (res.isSuccess) {
          alert(t('licenseManagement.messages.revokeSuccess'));
          dispatch(license.revokeLicenseItem(item.licenseKeyId));
        } else {
          licenseErrorHandler({
            code: res.error.code,
            showAlert: showAlert,
            t,
          });
          return;
        }
      }
    },
    [dispatch, showAlert, t]
  );

  const onSaveMemo = useCallback(
    (item: LicenseKeyListItemType) => async (memo: string) => {
      const res = await postAddLicenseKeyMemo({
        licenseKeyId: item.licenseKeyId,
        memo,
      });
      if (res.isSuccess) {
        dispatch(
          license.addLicenseItemMemo({
            licenseKeyId: item.licenseKeyId,
            memo: res.data.memo,
            memoIssuerName: res.data.issuerName,
            memoCreatedAt: res.data.createdAt,
          })
        );
      } else {
        licenseErrorHandler({ code: res.error.code, showAlert: showAlert, t });
        return;
      }
    },
    [dispatch, showAlert, t]
  );

  const valueRenderMap: Record<
    (typeof columns)[number]['key'],
    (row: LicenseKeyListItemType) => React.ReactNode
  > = {
    no: (row) => row.no,
    licenseKeyId: (row) => row.licenseKeyId,
    module: (row) => row.module,
    status: (row) => {
      const statusMap = {
        REGISTERED: t('licenseManagement.status.registered'),
        UNREGISTERED: t('licenseManagement.status.unregistered'),
        REVOKED: t('licenseManagement.status.revoked'),
      };

      return (
        <StatusBadge $status={row.status}>{statusMap[row.status]}</StatusBadge>
      );
    },
    createdAt: (row) => formatDate({ dateString: row.createdAt }),
    registeredAt: (row) =>
      row.registeredAt ? formatDate({ dateString: row.registeredAt }) : '-',
    revokedAt: (row) =>
      row.revokedAt ? formatDate({ dateString: row.revokedAt }) : '-',
    usedByMemberLoginId: (row) => row.usedByMemberLoginId ?? '-',
    issuerName: (row) => row.issuerName,
    issuanceReason: (row) => row.issuanceReason,
    memo: (row) => (
      <LicenseKeyMemoCell
        memo={row.memo}
        memoIssuerName={row.issuerName}
        memoCreatedAt={row.memoCreatedAt}
        onSave={onSaveMemo(row)}
      />
    ),
    delete: (row) => (
      <DeleteCell $show={isDeleteMode && row.status === 'REGISTERED'}>
        <CancelButton
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClickDelete(row)();
          }}
        />
      </DeleteCell>
    ),
  };

  const visibleColumns = columns.filter(
    (item) => !(item.key === 'delete' && !isDeleteMode)
  );
  return (
    <TableWrap>
      <TableElement>
        <Thead>
          <tr>
            {columns.map((col) => {
              const isLast =
                visibleColumns[visibleColumns.length - 1].key === col.key;

              return (
                <Th
                  key={col.key}
                  $width={col.width}
                  $hidden={col.key === 'delete' && !isDeleteMode}
                  $isLast={isLast}
                >
                  {col.label}
                </Th>
              );
            })}
          </tr>
        </Thead>

        {items.length > 0 && (
          <tbody>
            {items.map((row) => (
              <Tr key={row.licenseKeyId}>
                {columns.map((col) => (
                  <Td
                    key={col.key}
                    $align={col.align}
                    $hidden={col.key === 'delete' && !isDeleteMode}
                  >
                    {valueRenderMap[col.key](row)}
                  </Td>
                ))}
              </Tr>
            ))}
          </tbody>
        )}
      </TableElement>

      {items.length === 0 && (
        <NoneContainer>
          <NoneImg src={Images.NoneDataImg} alt="license-key-list-none" />
          <div>{t('common.noData')}</div>
        </NoneContainer>
      )}
    </TableWrap>
  );
};

export default LicenseKeyListItem;

const TableWrap = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-x: auto;
`;

const TableElement = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  min-width: 1500px;
`;

const Thead = styled.thead`
  background: #e5ecff;
`;

interface ThProps {
  $width: string;
  $hidden?: boolean;
  $isLast?: boolean;
}

const Th = styled.th<ThProps>`
  width: ${({ $width }) => $width};
  padding: 15px 20px;
  text-align: center;

  font-family: ${fonts.family.poppins};
  font-weight: ${fonts.weight.semiBold};
  font-size: 16px;
  line-height: 120%;
  color: #727883;

  transition: all 0.25s ease;

  ${({ $hidden }) =>
    $hidden &&
    `
    width: 0;
    padding: 0;
    opacity: 0;
  `}

  &:first-child {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }

  ${({ $isLast }) =>
    $isLast &&
    `
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
  `}
`;

interface TdProps {
  $align: string;
  $hidden?: boolean;
}

const Td = styled.td<TdProps>`
  padding: 12px 16px;
  text-align: ${({ $align }) => $align};
  vertical-align: middle;
  border-bottom: 1px solid #e5e5e5;

  font-family: ${fonts.family.poppins};
  font-size: 14px;
  line-height: 140%;
  color: #000000;

  white-space: normal;
  word-break: break-all;
  overflow-wrap: break-word;

  transition: all 0.25s ease;

  ${({ $hidden }) =>
    $hidden &&
    `
    width: 0;
    padding: 0;
    opacity: 0;
    border-bottom: none;
  `}
`;

const Tr = styled.tr`
  &:hover {
    background: rgba(118, 155, 247, 0.1);
  }
`;

const DeleteCell = styled.div<{ $show: boolean }>`
  display: flex;
  justify-content: center;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  pointer-events: ${({ $show }) => ($show ? 'auto' : 'none')};
  transition: opacity 0.25s ease;
`;

const CancelButton = styled.button`
  width: 36px;
  height: 36px;
  background-image: url(${Images.DeleteImg});
  background-repeat: no-repeat;
  background-position: center;
  background-size: 20px;
  border: none;
  background-color: transparent;
`;

const NoneContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;

  font-family: ${fonts.family.inter};
  font-size: 20px;
  line-height: normal;
  color: #000000;
`;

const NoneImg = styled.img`
  width: 163px;
  height: 163px;
`;

const StatusBadge = styled.span<{
  $status: LicenseKeyListItemType['status'];
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  min-width: 60px;
  padding: 4px 10px;

  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;

  background: ${({ $status }) => {
    switch ($status) {
      case 'REGISTERED':
        return '#dcfce7';
      case 'UNREGISTERED':
        return '#dbeafe';
      case 'REVOKED':
        return '#fee2e2';
    }
  }};

  color: ${({ $status }) => {
    switch ($status) {
      case 'REGISTERED':
        return '#15803d';
      case 'UNREGISTERED':
        return '#2563eb';
      case 'REVOKED':
        return '#dc2626';
    }
  }};
`;
